import React, { useEffect, useState } from "react";
import { api, inr } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Download, Send, Trash2, IndianRupee, Receipt, Loader2, X } from "lucide-react";

const STATUS_COLORS = {
  draft: "bg-gray-100 text-gray-700 border-gray-300",
  sent: "bg-blue-100 text-blue-700 border-blue-300",
  partial_paid: "bg-amber-100 text-amber-700 border-amber-300",
  paid: "bg-emerald-100 text-emerald-700 border-emerald-300",
  overdue: "bg-rose-100 text-rose-700 border-rose-300",
  cancelled: "bg-slate-100 text-slate-500 border-slate-300",
};

export default function Invoices() {
  const [list, setList] = useState([]);
  const [stats, setStats] = useState({ by_status: {} });
  const [showCreate, setShowCreate] = useState(false);
  const [paying, setPaying] = useState(null);

  const load = () => {
    api.get("/sales/invoices").then(r => setList(r.data || []));
    api.get("/sales/invoices/stats").then(r => setStats(r.data || {})).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const create = async (payload) => {
    try {
      await api.post("/sales/invoices", payload);
      toast.success("Invoice created");
      setShowCreate(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const sendInvoice = async (id) => {
    try { await api.post(`/sales/invoices/${id}/send`, { channels: ["email", "portal"] }); toast.success("Sent"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Cancel this invoice?")) return;
    try { await api.delete(`/sales/invoices/${id}`); toast.success("Cancelled"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-5" data-testid="admin-invoices">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-2xl font-bold text-[#1a3c5e]">Invoices</h2>
        <button onClick={() => setShowCreate(true)} data-testid="inv-create-btn" className="inline-flex items-center gap-1.5 bg-[#F7941D] hover:bg-[#e0861a] text-white px-4 py-2 rounded-xl text-sm font-semibold">
          <Plus className="w-4 h-4" /> New Invoice
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Total Billed" value={inr(stats.total_billed || 0)} icon={Receipt} tone="blue" testid="inv-stat-billed" />
        <Stat label="Collected" value={inr(stats.total_collected || 0)} icon={IndianRupee} tone="emerald" testid="inv-stat-collected" />
        <Stat label="Outstanding" value={inr(stats.total_outstanding || 0)} icon={Receipt} tone={stats.total_outstanding > 0 ? "rose" : "gray"} testid="inv-stat-out" />
        <Stat label="Invoices" value={stats.total_count || 0} icon={Receipt} tone="orange" testid="inv-stat-count" />
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FEF3E2] text-[#1a3c5e] text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-3">#</th>
              <th className="text-left px-4 py-3">Client</th>
              <th className="text-left px-4 py-3">Issue</th>
              <th className="text-left px-4 py-3">Due</th>
              <th className="text-right px-4 py-3">Total</th>
              <th className="text-right px-4 py-3">Paid</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-gray-500">No invoices yet.</td></tr>}
            {list.map(inv => (
              <tr key={inv.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`inv-row-${inv.id}`}>
                <td className="px-4 py-3 font-mono text-xs">{inv.invoice_no}</td>
                <td className="px-4 py-3"><div className="font-semibold">{inv.client?.name}</div><div className="text-xs text-gray-500">{inv.client?.phone || inv.client?.email}</div></td>
                <td className="px-4 py-3 text-xs">{inv.issue_date}</td>
                <td className="px-4 py-3 text-xs">{inv.due_date}</td>
                <td className="px-4 py-3 text-right font-semibold">{inr(inv.grand_total || 0)}</td>
                <td className="px-4 py-3 text-right">{inr(inv.amount_paid || 0)}</td>
                <td className="px-4 py-3"><span className={`text-[10px] uppercase px-2 py-0.5 rounded-full border ${STATUS_COLORS[inv.status]}`}>{(inv.status || "").replace("_", " ")}</span></td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <a target="_blank" rel="noreferrer" href={`${api.defaults.baseURL}/sales/invoices/${inv.id}/pdf`} title="PDF" data-testid={`inv-pdf-${inv.id}`} className="p-2 hover:bg-gray-100 rounded-lg"><Download className="w-4 h-4 text-[#1a3c5e]" /></a>
                    <button title="Send" data-testid={`inv-send-${inv.id}`} onClick={() => sendInvoice(inv.id)} className="p-2 hover:bg-gray-100 rounded-lg"><Send className="w-4 h-4 text-[#F7941D]" /></button>
                    {inv.status !== "paid" && inv.status !== "cancelled" && (
                      <button title="Record Payment" data-testid={`inv-pay-${inv.id}`} onClick={() => setPaying(inv)} className="p-2 hover:bg-emerald-50 rounded-lg"><IndianRupee className="w-4 h-4 text-emerald-600" /></button>
                    )}
                    {inv.status !== "paid" && <button title="Cancel" data-testid={`inv-del-${inv.id}`} onClick={() => remove(inv.id)} className="p-2 hover:bg-rose-50 rounded-lg"><Trash2 className="w-4 h-4 text-rose-600" /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onSubmit={create} />}
      {paying && <PayModal inv={paying} onClose={() => setPaying(null)} onPaid={() => { setPaying(null); load(); }} />}
    </div>
  );
}

function Stat({ label, value, icon: Icon, tone, testid }) {
  const tones = {
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    rose: "bg-rose-50 border-rose-200 text-rose-700",
    orange: "bg-orange-50 border-orange-200 text-orange-700",
    gray: "bg-gray-50 border-gray-200 text-gray-700",
  };
  return (
    <div className={`p-4 rounded-2xl border ${tones[tone]}`} data-testid={testid}>
      <Icon className="w-5 h-5 mb-1" />
      <div className="text-xs uppercase font-semibold opacity-70">{label}</div>
      <div className="text-xl font-bold mt-0.5">{value}</div>
    </div>
  );
}

function CreateModal({ onClose, onSubmit }) {
  const [data, setData] = useState({
    client: { name: "", phone: "", email: "", address: "", city: "", state: "", gstin: "" },
    items: [{ description: "Design Service", qty: 1, unit_price: 10000, total: 10000 }],
    tax_rate: 18, due_days: 7, notes: "",
  });

  const updItem = (i, k, v) => setData(d => {
    const items = d.items.map((it, ix) => ix === i ? { ...it, [k]: v, total: k === "qty" || k === "unit_price" ? Number(k === "qty" ? v : it.qty) * Number(k === "unit_price" ? v : it.unit_price) : it.total } : it);
    return { ...d, items };
  });
  const addItem = () => setData(d => ({ ...d, items: [...d.items, { description: "", qty: 1, unit_price: 0, total: 0 }] }));

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-3 flex items-center justify-between">
          <h3 className="text-lg font-bold text-[#1a3c5e]">New Invoice</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Input label="Client Name *" testid="inv-c-name" value={data.client.name} onChange={v => setData(d => ({ ...d, client: { ...d.client, name: v } }))} />
            <Input label="Phone" testid="inv-c-phone" value={data.client.phone} onChange={v => setData(d => ({ ...d, client: { ...d.client, phone: v } }))} />
            <Input label="Email" testid="inv-c-email" value={data.client.email} onChange={v => setData(d => ({ ...d, client: { ...d.client, email: v } }))} />
            <Input label="GSTIN" testid="inv-c-gst" value={data.client.gstin} onChange={v => setData(d => ({ ...d, client: { ...d.client, gstin: v } }))} />
            <Input label="Address" testid="inv-c-addr" className="col-span-2" value={data.client.address} onChange={v => setData(d => ({ ...d, client: { ...d.client, address: v } }))} />
            <Input label="City" testid="inv-c-city" value={data.client.city} onChange={v => setData(d => ({ ...d, client: { ...d.client, city: v } }))} />
            <Input label="State" testid="inv-c-state" value={data.client.state} onChange={v => setData(d => ({ ...d, client: { ...d.client, state: v } }))} />
          </div>
          <div>
            <div className="text-xs font-semibold uppercase text-gray-500 mb-2">Items</div>
            {data.items.map((it, i) => (
              <div key={i} className="flex items-center gap-2 mb-1">
                <input placeholder="Description" value={it.description} onChange={e => updItem(i, "description", e.target.value)} className="flex-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm" />
                <input type="number" value={it.qty} onChange={e => updItem(i, "qty", e.target.value)} className="w-16 border border-gray-300 rounded-lg px-2 py-1.5 text-sm" />
                <input type="number" value={it.unit_price} onChange={e => updItem(i, "unit_price", e.target.value)} className="w-24 border border-gray-300 rounded-lg px-2 py-1.5 text-sm" />
              </div>
            ))}
            <button onClick={addItem} className="text-xs text-[#F7941D]">+ Add item</button>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Input label="Tax Rate %" type="number" testid="inv-tax" value={data.tax_rate} onChange={v => setData(d => ({ ...d, tax_rate: Number(v) }))} />
            <Input label="Due Days" type="number" testid="inv-due-days" value={data.due_days} onChange={v => setData(d => ({ ...d, due_days: Number(v) }))} />
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-5 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl hover:bg-gray-100">Cancel</button>
          <button onClick={() => onSubmit(data)} data-testid="inv-submit" className="px-5 py-2 bg-[#F7941D] hover:bg-[#e0861a] text-white text-sm font-bold rounded-xl">Create</button>
        </div>
      </div>
    </div>
  );
}

function PayModal({ inv, onClose, onPaid }) {
  const [amount, setAmount] = useState(inv.balance_due || inv.grand_total);
  const [method, setMethod] = useState("UPI");
  const [ref, setRef] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try { await api.post(`/sales/invoices/${inv.id}/pay`, { amount: Number(amount), method, payment_reference: ref }); toast.success("Payment recorded"); onPaid(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full">
        <div className="border-b border-gray-200 p-4 flex items-center justify-between"><h3 className="font-bold text-[#1a3c5e]">Record Payment — {inv.invoice_no}</h3><button onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="p-4 space-y-3">
          <Input label="Amount *" type="number" testid="inv-pay-amount" value={amount} onChange={setAmount} />
          <label><div className="text-xs text-gray-600 mb-1">Method</div>
            <select value={method} onChange={e => setMethod(e.target.value)} data-testid="inv-pay-method" className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm"><option>UPI</option><option>Razorpay</option><option>Bank Transfer</option><option>Cash</option></select></label>
          <Input label="Reference" testid="inv-pay-ref" value={ref} onChange={setRef} />
        </div>
        <div className="border-t border-gray-200 p-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl hover:bg-gray-100">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="inv-pay-submit" className="px-5 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold rounded-xl inline-flex items-center gap-1">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Record Payment</button>
        </div>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, testid, type = "text", className = "" }) {
  return <label className={className}><div className="text-xs text-gray-600 mb-1">{label}</div><input type={type} value={value || ""} onChange={e => onChange(e.target.value)} data-testid={testid} className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-[#F7941D]" /></label>;
}
